import React, { useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Plus, Trash2, Edit2, X, Download, Star, AlignLeft, List as ListIcon } from 'lucide-react';
import { useFeedback, FeedbackForm, FeedbackField } from './FeedbackContext';
import { Toggle } from './Toggle';
type Tab = 'forms' | 'responses';
export function FeedbackAdminPage() {
  const {
    forms,
    responses,
    addForm,
    updateForm,
    deleteForm
  } = useFeedback();
  const [activeTab, setActiveTab] = useState<Tab>('forms');
  const [editingForm, setEditingForm] = useState<FeedbackForm | null>(null);
  const handleExportCSV = () => {
    if (responses.length === 0) return;
    const headers = ['Response ID', 'Form ID', 'Submitted At', 'Answers (JSON)'];
    const rows = responses.map(r => [r.id, r.formId, new Date(r.submittedAt).toLocaleString(), JSON.stringify(r.answers).replace(/"/g, '""')]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feedback_responses_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  return <div className="flex-1 overflow-y-auto bg-[#f9fafb] p-4 md:p-8" data-id="element-1833">
      <div className="max-w-5xl mx-auto" data-id="element-1834">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-1835">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3" data-id="element-1836">
            <div className="p-2 bg-orange-100 text-orange-600 rounded-lg" data-id="element-1837">
              <MessageSquare size={24} data-id="element-1838" />
            </div>
            Feedback
          </h1>
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg" data-id="element-1839">
            <button onClick={() => setActiveTab('forms')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${activeTab === 'forms' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1840">
              Forms
            </button>
            <button onClick={() => setActiveTab('responses')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${activeTab === 'responses' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1841">
              Responses
            </button>
          </div>
        </div>

        {activeTab === 'forms' && <div className="space-y-6" data-id="element-1842">
            <div className="flex justify-end" data-id="element-1843">
              <button onClick={() => setEditingForm({
            id: '',
            name: 'New Form',
            active: false,
            fields: []
          })} className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors" data-id="element-1844">
                <Plus size={18} data-id="element-1845" />
                Create Form
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1846">
              {forms.map(form => <div key={form.id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col" data-id="element-1847">
                  <div className="flex items-start justify-between mb-4" data-id="element-1848">
                    <div data-id="element-1849">
                      <h3 className="font-bold text-gray-900 text-lg" data-id="element-1850">
                        {form.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-1851">
                        {form.fields.length} field
                        {form.fields.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    <Toggle checked={form.active} onChange={active => updateForm(form.id, {
                active
              })} data-id="element-1852" />
                  </div>
                  <div className="mt-auto pt-4 flex items-center justify-end gap-2 border-t border-gray-100" data-id="element-1853">
                    <button onClick={() => deleteForm(form.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete form" data-id="element-1854">
                      <Trash2 size={18} data-id="element-1855" />
                    </button>
                    <button onClick={() => setEditingForm(form)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors" data-id="element-1856">
                      <Edit2 size={16} data-id="element-1857" />
                      Edit
                    </button>
                  </div>
                </div>)}
              {forms.length === 0 && <div className="col-span-full py-12 text-center border-2 border-dashed border-gray-200 rounded-xl" data-id="element-1858">
                  <MessageSquare size={32} className="mx-auto text-gray-300 mb-3" data-id="element-1859" />
                  <h3 className="text-gray-900 font-semibold" data-id="element-1860">No forms yet</h3>
                  <p className="text-gray-500 text-sm mt-1" data-id="element-1861">
                    Create a form to start collecting feedback.
                  </p>
                </div>}
            </div>
          </div>}

        {activeTab === 'responses' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-1862">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-1863">
              <h2 className="font-semibold text-gray-900" data-id="element-1864">All Responses</h2>
              <button onClick={handleExportCSV} disabled={responses.length === 0} className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors" data-id="element-1865">
                <Download size={16} data-id="element-1866" />
                Export CSV
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-1867">
              <table className="w-full text-left text-sm" data-id="element-1868">
                <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200" data-id="element-1869">
                  <tr data-id="element-1870">
                    <th className="px-4 py-3" data-id="element-1871">Date</th>
                    <th className="px-4 py-3" data-id="element-1872">Form</th>
                    <th className="px-4 py-3" data-id="element-1873">Customer</th>
                    <th className="px-4 py-3" data-id="element-1874">Summary</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-1875">
                  {responses.length === 0 ? <tr data-id="element-1876">
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500" data-id="element-1877">
                        No responses yet.
                      </td>
                    </tr> : responses.sort((a, b) => b.submittedAt - a.submittedAt).map(r => {
                const form = forms.find(f => f.id === r.formId);
                const summary = Object.entries(r.answers).map(([k, v]) => {
                  const field = form?.fields.find(f => f.id === k);
                  return `${field?.label || k}: ${v}`;
                }).join(' | ');
                return <tr key={r.id} className="hover:bg-gray-50" data-id="element-1878">
                            <td className="px-4 py-3 text-gray-900 whitespace-nowrap" data-id="element-1879">
                              {new Date(r.submittedAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-gray-600" data-id="element-1880">
                              {form?.name || 'Unknown Form'}
                            </td>
                            <td className="px-4 py-3 text-gray-600" data-id="element-1881">
                              {r.customerPhone || 'Guest'}
                            </td>
                            <td className="px-4 py-3 text-gray-500 truncate max-w-xs" data-id="element-1882">
                              {summary}
                            </td>
                          </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>}
      </div>

      <AnimatePresence data-id="element-1883">
        {editingForm && <FormEditorModal form={editingForm} onSave={f => {
        if (f.id) {
          updateForm(f.id, f);
        } else {
          addForm(f);
        }
        setEditingForm(null);
      }} onClose={() => setEditingForm(null)} data-id="element-1884" />}
      </AnimatePresence>
    </div>;
}
function FormEditorModal({
  form,
  onSave,
  onClose
}: {
  form: FeedbackForm;
  onSave: (form: FeedbackForm) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<FeedbackForm>(form);
  const addField = (type: FeedbackField['type']) => {
    setDraft(prev => ({
      ...prev,
      fields: [...prev.fields, {
        id: `field_${Math.random().toString(36).slice(2, 9)}`,
        type,
        label: 'New Question',
        required: false,
        options: type === 'single-select' ? ['Option 1', 'Option 2'] : undefined
      }]
    }));
  };
  const updateField = (id: string, patch: Partial<FeedbackField>) => {
    setDraft(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === id ? {
        ...f,
        ...patch
      } : f)
    }));
  };
  const removeField = (id: string) => {
    setDraft(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== id)
    }));
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1885">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-1886" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]" data-id="element-1887">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-1888">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1889">
            {form.id ? 'Edit Form' : 'New Form'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1890">
            <X size={20} data-id="element-1891" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-1892">
          <div data-id="element-1893">
            <label className="block text-sm font-semibold text-gray-700 mb-1" data-id="element-1894">
              Form Name
            </label>
            <input type="text" value={draft.name} onChange={e => setDraft({
            ...draft,
            name: e.target.value
          })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none" placeholder="e.g. Dining Experience" data-id="element-1895" />
          </div>

          <div data-id="element-1896">
            <div className="flex items-center justify-between mb-3" data-id="element-1897">
              <label className="block text-sm font-semibold text-gray-700" data-id="element-1898">
                Fields
              </label>
              <div className="flex gap-2" data-id="element-1899">
                <button onClick={() => addField('rating')} className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-orange-700 bg-orange-50 rounded hover:bg-orange-100" data-id="element-1900">
                  <Star size={12} data-id="element-1901" /> Rating
                </button>
                <button onClick={() => addField('text')} className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-orange-700 bg-orange-50 rounded hover:bg-orange-100" data-id="element-1902">
                  <AlignLeft size={12} data-id="element-1903" /> Text
                </button>
                <button onClick={() => addField('single-select')} className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-orange-700 bg-orange-50 rounded hover:bg-orange-100" data-id="element-1904">
                  <ListIcon size={12} data-id="element-1905" /> Select
                </button>
              </div>
            </div>

            <div className="space-y-3" data-id="element-1906">
              {draft.fields.map((field, idx) => <div key={field.id} className="p-4 border border-gray-200 rounded-xl bg-gray-50 relative group" data-id="element-1907">
                  <button onClick={() => removeField(field.id)} className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-white rounded-md opacity-0 group-hover:opacity-100 transition-all" data-id="element-1908">
                    <Trash2 size={16} data-id="element-1909" />
                  </button>
                  <div className="flex items-center gap-2 mb-3 pr-8" data-id="element-1910">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider w-16" data-id="element-1911">
                      {field.type}
                    </span>
                    <input type="text" value={field.label} onChange={e => updateField(field.id, {
                  label: e.target.value
                })} className="flex-1 bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500 outline-none" placeholder="Question text" data-id="element-1912" />
                  </div>
                  <div className="flex items-center gap-4 pl-18" data-id="element-1913">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer" data-id="element-1914">
                      <input type="checkbox" checked={field.required} onChange={e => updateField(field.id, {
                    required: e.target.checked
                  })} className="rounded text-orange-600 focus:ring-orange-500" data-id="element-1915" />
                      Required
                    </label>
                  </div>
                  {field.type === 'single-select' && <div className="mt-3 pl-18" data-id="element-1916">
                      <input type="text" value={field.options?.join(', ') || ''} onChange={e => updateField(field.id, {
                  options: e.target.value.split(',').map(s => s.trim())
                })} className="w-full bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500 outline-none" placeholder="Options (comma separated)" data-id="element-1917" />
                    </div>}
                </div>)}
              {draft.fields.length === 0 && <div className="text-center py-6 text-sm text-gray-500 border border-dashed border-gray-300 rounded-xl" data-id="element-1918">
                  No fields added yet.
                </div>}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end gap-3" data-id="element-1919">
          <button onClick={onClose} className="px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 rounded-xl transition-colors" data-id="element-1920">
            Cancel
          </button>
          <button onClick={() => onSave(draft)} disabled={!draft.name.trim() || draft.fields.length === 0} className="px-5 py-2.5 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-xl disabled:opacity-50 transition-colors" data-id="element-1921">
            Save Form
          </button>
        </div>
      </motion.div>
    </div>;
}